import { useState } from "react";
import {
  MessageCircle,
  Mail,
  Phone,
  Instagram,
  Youtube,
  MapPin,
  CheckCircle2,
  Loader2,
  Send,
  User as UserIcon,
  ArrowUpRight,
} from "lucide-react";
import RightAdRail from "@/components/RightAdRail";
import { api, formatApiError } from "@/lib/api";

const PHONE_INTL = "+91 99681 10261";
const PHONE_WA = "919968110261";
const EMAIL = "smriti.percept@gmail.com";

function ContactCard({ icon: Icon, label, value, href, external, testId, accent }) {
  const content = (
    <div className="flex items-center gap-4">
      <div
        className={`w-12 h-12 rounded-2xl flex items-center justify-center ${accent}`}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5B6079]">
          {label}
        </div>
        <div className="font-display font-bold text-[#1F2547] truncate">
          {value}
        </div>
      </div>
      <ArrowUpRight className="w-4 h-4 text-[#5B6079] shrink-0" />
    </div>
  );
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      data-testid={testId}
      className="block bg-white rounded-2xl brutal-shadow-sm p-5 hover-pop"
    >
      {content}
    </a>
  );
}

function isValidPhone(p) {
  if (!p) return true; // optional
  const digits = p.replace(/[^\d+]/g, "").replace(/^\+/, "");
  return digits.length >= 8 && digits.length <= 15;
}

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) return setErr("Please enter your name.");
    if (!form.message.trim()) return setErr("Please write a short message.");
    if (form.phone && !isValidPhone(form.phone))
      return setErr("Please enter a valid phone number.");
    if (!form.phone && !form.email)
      return setErr("Please share either a phone number or an email so we can reply.");

    setBusy(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone || "0000000000", // backend requires a phone; use placeholder if email only
        email: form.email || null,
        message: form.message,
        class_or_age: null,
        city: null,
        course_id: null,
        course_title: "General enquiry",
      };
      await api.post("/leads", payload);
      setSuccess(true);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main data-testid="contact-page" className="min-h-[60vh]">
      <RightAdRail />

      {/* Hero */}
      <section className="bg-[#C7DCF7]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#1F2547]/70">
            / Contact
          </span>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-tighter mt-3 text-[#1F2547] leading-[1]">
            Let&apos;s talk.
          </h1>
          <p className="mt-4 text-base md:text-lg text-[#1F2547]/75 font-medium max-w-2xl">
            Have a question about our programs, or want to say hi? Drop a
            message — or ping us directly on WhatsApp, Instagram, or email.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="grid md:grid-cols-5 gap-8 md:gap-10 items-start">
          {/* Left column — verified contact details */}
          <aside className="md:col-span-2 space-y-4">
            <div className="mb-2">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#9F87D8]">
                / Reach us directly
              </span>
              <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight text-[#1F2547] mt-1">
                Verified channels
              </h2>
            </div>

            <ContactCard
              icon={MessageCircle}
              label="WhatsApp (fastest reply)"
              value={PHONE_INTL}
              href={`https://wa.me/${PHONE_WA}?text=Hi%20Percept%20Academy%2C%20I%27d%20like%20to%20know%20more.`}
              external
              testId="contact-whatsapp"
              accent="bg-[#D4EDDB] text-[#1F2547]"
            />
            <ContactCard
              icon={Phone}
              label="Call"
              value={PHONE_INTL}
              href={`tel:${PHONE_WA.replace(/^91/, "+91")}`}
              testId="contact-phone"
              accent="bg-[#EFE9F7] text-[#9F87D8]"
            />
            <ContactCard
              icon={Mail}
              label="Email"
              value={EMAIL}
              href={`mailto:${EMAIL}`}
              testId="contact-email"
              accent="bg-[#C7DCF7] text-[#1F2547]"
            />
            <ContactCard
              icon={Instagram}
              label="Instagram"
              value="@perceptacademy_"
              href="https://www.instagram.com/perceptacademy_?igsh=MWo1Y281c216N2p5OQ=="
              external
              testId="contact-instagram"
              accent="bg-[#9F87D8] text-white"
            />
            <ContactCard
              icon={Youtube}
              label="YouTube"
              value="@SmritiAsthana"
              href="https://www.youtube.com/@SmritiAsthana"
              external
              testId="contact-youtube"
              accent="bg-[#1F2547] text-white"
            />

            <div className="rounded-2xl bg-[#EFE9F7] p-5 mt-2">
              <div className="flex items-center gap-2 mb-1">
                <MapPin className="w-4 h-4 text-[#9F87D8]" />
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5B6079]">
                  Where
                </div>
              </div>
              <div className="font-display font-bold text-[#1F2547]">
                100% online · learners worldwide
              </div>
              <p className="text-xs text-[#5B6079] font-medium mt-1">
                Classes run over live video — join from any city.
              </p>
            </div>
          </aside>

          {/* Right column — message form */}
          <div className="md:col-span-3">
            {success ? (
              <div
                data-testid="contact-success"
                className="bg-white rounded-3xl brutal-shadow p-8 md:p-10 text-center"
              >
                <div className="w-16 h-16 rounded-full bg-[#D4EDDB] text-[#1F2547] flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-black tracking-tight text-[#1F2547]">
                  Thanks — we&apos;ve got your message.
                </h3>
                <p className="mt-3 text-[#5B6079] font-medium max-w-md mx-auto">
                  We&apos;ll respond within one working day. If it&apos;s
                  urgent, please ping us directly on WhatsApp.
                </p>
                <a
                  href={`https://wa.me/${PHONE_WA}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-success-whatsapp"
                  className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-[#9F87D8] text-white font-bold rounded-full brutal-shadow-sm btn-press"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp us now
                </a>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="bg-white rounded-3xl brutal-shadow p-6 md:p-8 space-y-5"
              >
                <div>
                  <h2 className="font-display font-black text-2xl text-[#1F2547]">
                    Send us a message
                  </h2>
                  <p className="text-sm text-[#5B6079] font-medium mt-1">
                    We reply within one working day.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#5B6079] mb-1.5">
                    Full name *
                  </label>
                  <div className="relative">
                    <UserIcon className="w-4 h-4 text-[#5B6079] absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      data-testid="contact-field-name"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full pl-9 pr-3 py-3 rounded-xl border border-[#1F2547]/15 focus:outline-none focus:border-[#9F87D8] text-[#1F2547] font-medium"
                      placeholder="e.g. Aarav Sharma"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#5B6079] mb-1.5">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-[#5B6079] absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        data-testid="contact-field-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full pl-9 pr-3 py-3 rounded-xl border border-[#1F2547]/15 focus:outline-none focus:border-[#9F87D8] text-[#1F2547] font-medium"
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#5B6079] mb-1.5">
                      Phone / WhatsApp
                    </label>
                    <div className="relative">
                      <Phone className="w-4 h-4 text-[#5B6079] absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        data-testid="contact-field-phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="w-full pl-9 pr-3 py-3 rounded-xl border border-[#1F2547]/15 focus:outline-none focus:border-[#9F87D8] text-[#1F2547] font-medium"
                        placeholder="+91 98765 43210"
                      />
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-[#5B6079] -mt-3 font-medium">
                  Share at least one — email or phone — so we can reply.
                </p>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#5B6079] mb-1.5">
                    Your message *
                  </label>
                  <textarea
                    data-testid="contact-field-message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full px-3 py-3 rounded-xl border border-[#1F2547]/15 focus:outline-none focus:border-[#9F87D8] text-[#1F2547] font-medium"
                    placeholder="Tell us what you'd like to know…"
                  />
                </div>

                {err && (
                  <div
                    data-testid="contact-form-error"
                    className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 font-medium"
                  >
                    {err}
                  </div>
                )}

                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#9F87D8] text-white font-bold rounded-full brutal-shadow btn-press disabled:opacity-60"
                >
                  {busy ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Send message
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
